import PageWrapper from "@/components/PageWrapper.tsx";
import { Input } from "@/components/ui/input.tsx";
import { Suspense, useState } from "react";
import BaseButton from "@/components/base/button/BaseButton.tsx";
import { useTranslation } from "react-i18next";
import EyeIcon from "@/assets/icons/eye.svg?react";
import { openLink } from "@telegram-apps/sdk-react";
import ExternalLink from "@/assets/icons/external-link.svg?react";
import GetDoneSuccessIcon from "@/assets/icons/getdon_success.svg?react";
import { cn } from "@/lib/utils";
import { useNavigate, useParams } from "react-router";
import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import { useFetch } from "@/lib/axios.config";
import {
  TaskAwardResponse,
  TaskStatusResponse,
} from "@/pages/get-don/tasks.types";
import { toast } from "sonner";
import { formatNumber } from "@/shared/utils";
import { boom } from "@/lib/exposion";
import { useTaskDetailOptions } from "@/pages/get-don/tasks.api";
import TaskLoading from "@/pages/get-don/components/cards/GetDonCardLoading";

async function getAwardFn(taskId: string): Promise<TaskAwardResponse> {
  const response = await useFetch.post<{ state: TaskAwardResponse }>(
    `/tasks/GetTaskAward/${taskId}`
  );
  return response.data.state;
}

async function claimAward(
  taskId: string,
  input?: string
): Promise<TaskStatusResponse> {
  const response = await useFetch.post<{ state: TaskStatusResponse }>(
    `/tasks/ClaimTaskAward/${taskId}`,
    {
      keyword: input ?? "",
    }
  );
  return response.data.state;
}

const PromoCodeLoading = () => {
  return (
    <div className="container flex justify-between flex-col py-6">
      <div className="grid gap-3 overflow-hidden">
        <TaskLoading />
        <div className="mt-6">
          <div className="skeleton w-1/3 h-[22px] rounded-sm mb-1" />
          <div className="skeleton w-full h-11 rounded-10" />
        </div>
      </div>
    </div>
  );
};

const PromoCode = () => {
  return (
    <Suspense fallback={<PromoCodeLoading />}>
      <BasePromoCode />
    </Suspense>
  );
};

const BasePromoCode = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { taskId } = useParams();
  const [inputValue, setInputValue] = useState("");
  const { data: task } = useSuspenseQuery(useTaskDetailOptions(taskId));

  const { mutateAsync: claimReward, isPending: isClaiming } = useMutation({
    mutationFn: () => {
      return claimAward(task.id ?? "", inputValue);
    },
    onSuccess() {
      // if (["INCOMPLETE", "RETRY_LATER"].includes(data)) {
      //   toast.error(t("incomplete_the_task"));
      // }
    },
  });

  const { mutateAsync: getAward, isPending: isVerifying } = useMutation({
    mutationFn: async () => {
      return await getAwardFn(task.id ?? "");
    },
    onSuccess: (data) => {
      if (data && data === "COMPLETED") {
        toast(t("congratulations"), {
          description: t("get_done_successfully", {
            don: formatNumber(task.award ?? ""),
          }),
          icon: <GetDoneSuccessIcon className="size-7" />,
        });

        boom();
      }
    },
  });

  const handleButtonClicked = async () => {
    if (!inputValue.length) return;
    const res = await claimReward();
    if (res === "COMPLETED" || res === "READY_AWARD") {
      const res = await getAward();
      if (res === "COMPLETED") {
        localStorage.removeItem("promo-code-task");
        navigate(-1);
      }
    } else {
      toast.error(t("incomplete_the_promo_task"));
    }
  };

  const isPending = isClaiming || isVerifying;

  return (
    <PageWrapper>
      <div className="container flex justify-between flex-col py-6">
        <div className="p-3 border border-white/08 rounded-10 bg-dark mb-6">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <span className="size-9 rounded-full bg-white/08 flex-center">
                <EyeIcon className="size-6 text-white" strokeWidth={2} />
              </span>
              <div className="space-y-0.5">
                <h2 className="text-sm font-semibold leading-130">
                  {task.name}
                </h2>
                <p className="text-xs text-orange-100">
                  +{formatNumber(task.award)} DON
                </p>
              </div>
            </div>
            <span
              onClick={() => {
                openLink(task.link);
              }}
              className="flex-center size-8 cursor-pointer rounded-full bg-orange-100"
            >
              <ExternalLink className="size-5 text-dark" />
            </span>
          </div>
        </div>
        <div className="flex justify-between flex-col gap-2">
          <h2 className="font-semibold">{t("promo_code")}</h2>
          <Input
            type="text"
            placeholder={t("enter_promo_code")}
            className="border-white/08"
            value={inputValue}
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            onChange={(e) => setInputValue(e.target.value)}
          />
          <p className="text-sm text-gray-1">
            {t("check_in_promo_code_description")}
          </p>
        </div>
        <div className="fixed w-full bottom-safe-bottom left-0 right-0 container">
          <BaseButton
            text={t("task_status.PERFORMED")}
            className={cn("w-full")}
            onClick={handleButtonClicked}
            isLoading={isPending}
            disabled={inputValue === ""}
          />
        </div>
      </div>
    </PageWrapper>
  );
};

export default PromoCode;
